import ErrorNotifier, { Severity } from 'app/scripts/services/error-notifier';
import { TDispatch, TGetState } from 'state';
import { AnalyticsProvider } from 'app/scripts/services/analytics-provider';
import { Appearance, Folder, Message } from '../types';
import approveMessages from '../../api/approve';
import { approveItemsFailure, approveItemsStart, approveItemsSuccess } from '.';

export type Options = {
  // Set the appearance for the message after the action made.
  appearance: Appearance;
  // Indicates whether the actions was triggered by hot keys.
  fromKeyboard: boolean;
};

const defaultOptions: Options = {
  appearance: Appearance.COLLAPSED,
  fromKeyboard: false
};

const approveItems = (messages: Message[] = [], options: Options = defaultOptions) => {
  return async (dispatch: TDispatch, getState: TGetState) => {
    const { appearance } = options;
    const messageIds: string[] = messages.map((message: Message) => message.get('id'));

    dispatch(approveItemsStart({ messageIds, appearance, folder: Folder.APPROVED }));

    try {
      const { isModerationMode, pageInfo } = getState().moderation.messages;
      const usePrefix = isModerationMode && pageInfo.folder === Folder.PENDING;

      const { results } = await approveMessages(messageIds, usePrefix);

      const [successItems, failedItems] = getSuccessAndFailedItems(messageIds, results);

      if (successItems.length) {
        dispatch(
          approveItemsSuccess({ messageIds: successItems, appearance, folder: Folder.APPROVED })
        );
      }

      if (failedItems.length) {
        AnalyticsProvider.track('message approve error', {
          message_ids: messageIds,
          level: Severity.Critical
        });
        ErrorNotifier.notifyError('Message Approved Failed: Unresolved Messages from BED', {
          message_ids: failedItems
        });
        dispatch(
          approveItemsFailure({
            messageIds: failedItems,
            error: new Error('UnresolvedMessagesOnBED')
          })
        );
      }
    } catch (error) {
      console.error(error);
      ErrorNotifier.notifyError('Message Approve Failed', {
        level: Severity.Critical,
        message_ids: messageIds
      });
      AnalyticsProvider.track('message approve error', {
        message_ids: messageIds
      });
      dispatch(approveItemsFailure({ error, messageIds }));
    }
  };
};

export const getSuccessAndFailedItems = (
  messageIds: string[] = [],
  results: Record<string, boolean> = {}
): [string[], string[]] => {
  return messageIds.reduce(
    ([success, failed]: [string[], string[]], messageId: string): [string[], string[]] =>
      // Check for message status:
      // if succeeded, append to success list
      // if failed, append to failed list
      results[messageId] ? [[...success, messageId], failed] : [success, [...failed, messageId]],
    [[], []]
  );
};

export default approveItems;
